// =============================================================================
// useSitesSlice — typed access + actions for the Sites page store slice.
//
// Read a projection with a selector: `const order = useSitesSlice(s => s.order)`.
// Mutate via the exported action functions (module-level, no hook needed).
//
// The pending-edit buffer model lives here:
//   pendingEdits[rowId][field] = staged value   (a diff from the server row)
//   effectiveValue(row, field) = staged value if present, else the server value
// A field is dropped from the buffer when its staged value returns to the server
// original (mirrors the drawer's existing setVal behaviour), so the buffer only
// ever holds true diffs and "N pending changes" is always accurate.
// =============================================================================
import { getState, updateSites, type SitesSliceState } from './pageStore'
import { makeSliceHook } from './sliceHook'

// Selector-stability rule lives in sliceHook.ts (all slice hooks share it).
export const useSitesSlice = makeSliceHook('sites')

// Synchronous, non-hook read of the current sites slice (for imperative code like the
// mount-effect that must merge into the persisted state, not overwrite it).
export function getSitesSnapshot(): SitesSliceState {
  return getState().sites
}

// ---- One-time seed from meta (columns → order/hidden) + URL param seeds ------
// Guarded by `initialized` so re-mounts after navigation don't clobber user state.
export function initSitesSlice(seed: {
  order: string[]
  hidden: string[]
  colFilters: Record<string, string[]>
  search: string
  slicer: string
}): void {
  updateSites((s) => {
    if (s.initialized) return s
    return {
      ...s,
      initialized: true,
      order: seed.order,
      hidden: seed.hidden,
      colFilters: seed.colFilters,
      search: seed.search,
      slicer: seed.slicer,
    }
  })
}

// ---- View-state setters ------------------------------------------------------
export const setOrder = (order: string[]) => updateSites((s) => ({ ...s, order }))
export const setHidden = (hidden: string[]) => updateSites((s) => ({ ...s, hidden }))
export const setSort = (sort: string) => updateSites((s) => ({ ...s, sort }))
export const setDir = (dir: 'asc' | 'desc') => updateSites((s) => ({ ...s, dir }))
export const setSearch = (search: string) => updateSites((s) => ({ ...s, search }))
export const setSlicer = (slicer: string) => updateSites((s) => ({ ...s, slicer }))
export const setScroll = (scroll: number) => updateSites((s) => ({ ...s, scroll }))
export const setColFilters = (colFilters: Record<string, string[]>) => updateSites((s) => ({ ...s, colFilters }))

export function clearFilters(): void {
  updateSites((s) => ({ ...s, search: '', slicer: '', colFilters: {} }))
}

// ---- Pending-edit buffer -----------------------------------------------------

// True if a staged value equals the server original (so the field should be dropped).
// Typed inputs emit strings even for numeric columns, so the server's `1` (number)
// and the input's `"1"` (string) must compare equal — otherwise re-entering the same
// value stages a phantom no-op edit that would PUT on Apply. Dates already arrive as
// 'YYYY-MM-DD' strings (matching the date input), so plain === suffices for those.
// Exported so the drawer's draft-diff uses the SAME equality (no second definition).
// The buffer deliberately holds INPUT-shaped values (all three edit paths — inline, bulk,
// drawer — emit strings from typed inputs, so they're mutually consistent); this equality
// plus server coercion reconcile them. Verified to round-trip cleanly for every editable
// field type, so Apply→reload leaves no phantom pending state from form divergence.
export function isSameAsServer(value: unknown, serverValue: unknown): boolean {
  if (value === serverValue) return true
  if (value == null && serverValue == null) return true
  // Cross the number/numeric-string boundary: compare by coerced string form when
  // both sides are non-null primitives. Empty string is treated as null (unset).
  const a = value === '' ? null : value
  const b = serverValue === '' ? null : serverValue
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  return String(a) === String(b)
}

// Stage a single field edit on one row (diff-from-server). Dropping the field
// when it returns to the server value keeps the buffer to true diffs only.
export function stageEdit(rowId: number, field: string, value: unknown, serverValue: unknown): void {
  updateSites((s) => {
    const row = s.pendingEdits[rowId]
    const same = isSameAsServer(value, serverValue)

    if (same) {
      if (!row || !(field in row)) return s // nothing staged to remove
      const nextRow = { ...row }
      delete nextRow[field]
      const nextPending = { ...s.pendingEdits }
      if (Object.keys(nextRow).length === 0) delete nextPending[rowId]
      else nextPending[rowId] = nextRow
      return { ...s, pendingEdits: nextPending }
    }

    const nextRow = { ...(row ?? {}), [field]: value }
    return { ...s, pendingEdits: { ...s.pendingEdits, [rowId]: nextRow } }
  })
}

// Stamp one field/value across many rows into the buffer (bulk edit) in a SINGLE
// store update — one clone + one listener notification, not one per row (a select-all
// stamp over ~2000 rows would otherwise be O(N²) clones + N notifies). Merges field-by-
// field per row, so already-pending edits on OTHER fields of those rows survive; a value
// equal to a row's server original is dropped (or removes an existing pending entry).
export function stageMany(ids: number[], field: string, value: unknown, serverValueOf: (id: number) => unknown): void {
  updateSites((s) => {
    const nextPending = { ...s.pendingEdits }
    let changed = false
    for (const id of ids) {
      const same = isSameAsServer(value, serverValueOf(id))
      const row = nextPending[id]
      if (same) {
        if (row && field in row) {
          const nextRow = { ...row }; delete nextRow[field]
          if (Object.keys(nextRow).length === 0) delete nextPending[id]
          else nextPending[id] = nextRow
          changed = true
        }
      } else {
        nextPending[id] = { ...(row ?? {}), [field]: value }
        changed = true
      }
    }
    return changed ? { ...s, pendingEdits: nextPending } : s
  })
}

// Stage a set of drawer diffs (already computed as field→value) for one row in a
// SINGLE store update (one clone + one notify), not one per field — mirrors stageMany.
export function stageDrawer(rowId: number, diffs: Record<string, unknown>, serverValueOf: (field: string) => unknown): void {
  updateSites((s) => {
    const nextRow = { ...(s.pendingEdits[rowId] ?? {}) }
    let changed = false
    for (const [field, value] of Object.entries(diffs)) {
      if (isSameAsServer(value, serverValueOf(field))) {
        if (field in nextRow) { delete nextRow[field]; changed = true }
      } else { nextRow[field] = value; changed = true }
    }
    if (!changed) return s
    const nextPending = { ...s.pendingEdits }
    if (Object.keys(nextRow).length === 0) delete nextPending[rowId]
    else nextPending[rowId] = nextRow
    return { ...s, pendingEdits: nextPending }
  })
}

export function clearPending(): void {
  updateSites((s) => (Object.keys(s.pendingEdits).length === 0 ? s : { ...s, pendingEdits: {} }))
}

// Clear applied edits after a successful Apply — by field AND by the exact value that was
// sent. A field is only removed if its CURRENT buffer value still equals what the PUT sent;
// if the user re-staged that same field to a new value while the PUT was in flight, the
// newer value is kept (not clobbered). Clearing by field (not whole row) already preserves
// edits to OTHER fields staged mid-flight; the value check extends that to same-field re-stages.
// `sentByRow` maps rowId → { field: sentValue }. A row whose last pending field clears is removed.
export function clearFields(sentByRow: Record<number, Record<string, unknown>>): void {
  updateSites((s) => {
    const nextPending = { ...s.pendingEdits }
    let changed = false
    for (const [idStr, sentFields] of Object.entries(sentByRow)) {
      const id = Number(idStr)
      const row = nextPending[id]
      if (!row) continue
      const nextRow = { ...row }
      for (const [f, sentVal] of Object.entries(sentFields)) {
        // Only clear if unchanged since the PUT — a mid-flight re-stage of the same field survives.
        if (f in nextRow && nextRow[f] === sentVal) { delete nextRow[f]; changed = true }
      }
      if (Object.keys(nextRow).length === 0) delete nextPending[id]
      else nextPending[id] = nextRow
    }
    return changed ? { ...s, pendingEdits: nextPending } : s
  })
}

// ---- Derived helpers (pure, not hooks) --------------------------------------

// The value to SHOW/edit for a field: staged if pending, else the server row value.
export function effectiveValue(
  pendingEdits: Record<number, Record<string, unknown>>,
  rowId: number,
  field: string,
  serverValue: unknown,
): unknown {
  const row = pendingEdits[rowId]
  return row && field in row ? row[field] : serverValue
}

// Total staged field-edits across all rows (the "Apply N changes" count = cells).
export function pendingCellCount(pendingEdits: Record<number, Record<string, unknown>>): number {
  let n = 0
  for (const rowId in pendingEdits) n += Object.keys(pendingEdits[rowId]).length
  return n
}
